import { useRef, useState } from "react";
import { InterviewSession } from "../../Entities/interview-session.entity";
import {
  AIAssessmentDetails,
  AIAssessmentQuestion,
} from "../../Entities/ai-assessment.entity";
import { sassFalse } from "sass";

export function useAIAssessmentState() {
  const [assessmentInitialized,setAssessmentInitialized] = useState<boolean>(false)

  // State to track the current interview session
  const [currentSession, setCurrentSession] = useState<InterviewSession | null>(
    null
  );
  const [aiAssessmentQuestions, setAiAssessmentQuestions] = useState<
    AIAssessmentQuestion[]
  >([]);
  const [aiAssessmentDetails, setAiAssessmentDetails] =
    useState<AIAssessmentDetails | null>(null);
  const [temporaryAnswer, setTemporaryAnswer] = useState<string | null>(null);

  // state to track current AI assessment status
  const [isRoundFinished, setIsRoundFinished] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [isEvaluating, setIsEvaluating] = useState<boolean>(false);

  // state to track UI elements
  const [showCamRecorder, setShowCamRecorder] = useState<boolean>(false);
  const [pressedSuspiciousKey, setPressedSuspiciousKey] = useState<
    string | null
  >(null);
  const [exitFullScreenTimer, setExitFullScreenTimer] =
    useState<boolean>(false);
  const [showDiffrentDevice, setShowDiffrentDevice] = useState<boolean>(false);

  // state to track proctoring logic
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);

  // state and refs to track screen recording
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const recordedChunks = useRef<Blob[]>([]);

  // States to track Modals
  const [showRightClickDisabledModal, setShowRightClickDisabledModal] =
    useState<boolean>(false);
  const [showCursorFocusChangeModal, setShowCursorFocusChangeModal] =
    useState<boolean>(false);
  const [showTabSwitchModal, setShowTabSwitchModal] = useState<boolean>(false);
  const [showSuspiciousKeyPressModal, setShowSuspiciousKeyPressModal] =
    useState<boolean>(false);
  const [showExitFullScreenModal, setShowExitFullScreenModal] =
    useState<boolean>(false);

  // States to track start ai assessment screen
  const [showStartAiAssessmentScreen, setShowStartAiAssessmentScreen] =
    useState<boolean>(false);

  // State to track assessment timer start time (timestamp in milliseconds)
  const [assessmentStartTime, setAssessmentStartTime] = useState<number | null>(
    null
  );

  // States to track confirmation modals
  const [showRetryEvaluation, setShowRetryEvaluation] =
    useState<boolean>(false);
  const [showPermissionConfirmationModal, setShowPermissionConfirmationModal] =
    useState<boolean>(false);

  // Element ref to track the current element
  const fullscreenElementRef = useRef<HTMLDivElement>(null);
  const cursorFocuChangeModalRef = useRef<HTMLDivElement>(null);

  // Permission refs to track camera , screen and microphone permissions
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const displayStreamRef = useRef<MediaStream | null>(null);
  const cameraMicrophoneStreamRef = useRef<MediaStream | null>(null);

  const clearAIAssessmentState = () => {
    setCurrentSession(null);
    setIsRoundFinished(false);
    setAiAssessmentQuestions([]);
    setAiAssessmentDetails(null);
    setIsEvaluating(false);
    setTemporaryAnswer(null);
    setRecordedBlob(null);
    recordedChunks.current = [];
    setAssessmentStartTime(null);
    setAssessmentInitialized(false)
  };

  const clearProctoringState = () => {
    setIsFullscreen(false);
    setIsOffline(!navigator.onLine);
  };

  const clearUIState = () => {
    setShowCamRecorder(false);
    setPressedSuspiciousKey(null);
    setExitFullScreenTimer(false);
    setShowDiffrentDevice(false);
  };

  const clearModalState = () => {
    setShowRightClickDisabledModal(false);
    setShowCursorFocusChangeModal(false);
    setShowTabSwitchModal(false);
    setShowSuspiciousKeyPressModal(false);
    setShowExitFullScreenModal(false);
  };

  const clearExitFullScreenState = () => {
    setExitFullScreenTimer(false);
    setShowExitFullScreenModal(false);
  };

  return {
    assessmentInitialized,
    setAssessmentInitialized,
    currentSession,
    setCurrentSession,
    isRoundFinished,
    setIsRoundFinished,
    aiAssessmentQuestions,
    setAiAssessmentQuestions,
    aiAssessmentDetails,
    setAiAssessmentDetails,
    currentStep,
    setCurrentStep,
    isFullscreen,
    setIsFullscreen,
    isEvaluating,
    setIsEvaluating,
    showCamRecorder,
    setShowCamRecorder,
    showRightClickDisabledModal,
    setShowRightClickDisabledModal,
    showCursorFocusChangeModal,
    setShowCursorFocusChangeModal,
    showTabSwitchModal,
    setShowTabSwitchModal,
    fullscreenElementRef,
    cursorFocuChangeModalRef,
    temporaryAnswer,
    setTemporaryAnswer,
    pressedSuspiciousKey,
    setPressedSuspiciousKey,
    showSuspiciousKeyPressModal,
    setShowSuspiciousKeyPressModal,
    showExitFullScreenModal,
    setShowExitFullScreenModal,
    exitFullScreenTimer,
    setExitFullScreenTimer,
    isOffline,
    setIsOffline,
    mediaRecorderRef,
    displayStreamRef,
    cameraMicrophoneStreamRef,
    recordedBlob,
    setRecordedBlob,
    showStartAiAssessmentScreen,
    setShowStartAiAssessmentScreen,
    showRetryEvaluation,
    setShowRetryEvaluation,
    showPermissionConfirmationModal,
    setShowPermissionConfirmationModal,
    recordedChunks,
    clearAIAssessmentState,
    clearProctoringState,
    clearUIState,
    clearModalState,
    showDiffrentDevice,
    setShowDiffrentDevice,
    clearExitFullScreenState,
    assessmentStartTime,
    setAssessmentStartTime,
  };
}
